import React, { useState } from "react";

/**
 * PaymentDeclarationModal
 *
 * Renders a full-screen overlay modal with SBI payment gateway information.
 * The user must check "I Agree" before they can click "Proceed".
 *
 * Props:
 *  - onAgree  : () => void  – called when the user agrees and closes the modal
 *  - onCancel : () => void  – called when the user cancels (e.g. go back)
 */
const PaymentDeclarationModal = ({ onAgree, onCancel }) => {
  const [agreed, setAgreed] = useState(false);

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.55)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    boxSizing: "border-box",
  };

  const dialogStyle = {
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
    maxWidth: "640px",
    width: "100%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const headerStyle = {
    background: "#0E7693",
    color: "#fff",
    padding: "16px 20px",
    fontWeight: 700,
    fontSize: "18px",
    borderRadius: "8px 8px 0 0",
    flexShrink: 0,
  };

  const bodyStyle = {
    padding: "20px 24px",
    overflowY: "auto",
    flex: 1,
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#333",
  };

  const footerStyle = {
    padding: "14px 24px",
    borderTop: "1px solid #ddd",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    flexWrap: "wrap",
    gap: "12px",
  };

  const checkboxRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
  };

  const btnRow = {
    display: "flex",
    gap: "10px",
  };

  const baseBtnStyle = {
    padding: "8px 22px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    outline: "none",
  };

  const cancelBtnStyle = {
    ...baseBtnStyle,
    background: "#fff",
    color: "#0E7693",
    border: "1.5px solid #0E7693",
  };

  const proceedBtnStyle = {
    ...baseBtnStyle,
    background: agreed ? "#0E7693" : "#9DBEC7",
    color: "#fff",
    cursor: agreed ? "pointer" : "not-allowed",
    transition: "background 0.2s ease",
  };

  const bulletListStyle = {
    paddingLeft: "20px",
    margin: "6px 0 12px 0",
    listStyleType: "disc",
  };

  const sectionTitleStyle = {
    fontWeight: 700,
    marginTop: "14px",
    marginBottom: "4px",
    color: "#0E7693",
  };

  const noteBlockStyle = {
    background: "#FFF8E1",
    border: "1px solid #FFD54F",
    borderRadius: "4px",
    padding: "10px 14px",
    marginTop: "10px",
    fontSize: "13px",
    lineHeight: "1.6",
  };

  return (
    <div style={overlayStyle}>
      <div style={dialogStyle}>
        {/* Header */}
        <div style={headerStyle}>⚠ Important Payment Information</div>

        {/* Body */}
        <div style={bodyStyle}>
          <p style={{ marginTop: 0, marginBottom: "12px" }}>
            Before proceeding to the SBI Payment Gateway, please read the following information carefully:
          </p>

          <p style={sectionTitleStyle}>We provide 4 payment modes for making your payment:</p>
          <ul style={bulletListStyle}>
            <li>NEFT</li>
            <li>Credit/Debit Card</li>
            <li>UPI</li>
            <li>Net Banking</li>
          </ul>

          <p>
            Please confirm your preferred payment mode carefully before proceeding. Once a payment mode is
            selected, make sure it is the mode you intend to use.
          </p>

          <div style={noteBlockStyle}>
            <p style={{ margin: "0 0 8px 0", fontWeight: 700 }}>
              In case of NEFT challan creation:
            </p>

            <ul style={{ margin: 0, paddingLeft: "20px", listStyleType: "disc" }}>
              <li style={{ marginBottom: "8px" }}>
                The payment gateway supports NEFT payments only for this process.{" "}
                <strong>RTGS payments are not supported</strong>, and any payment made
                through RTGS will not be considered successful.
              </li>

              <li style={{ marginBottom: "8px" }}>
                Once you select NEFT and generate the challan, the{" "}
                <strong>payment mode cannot be changed for 7 days.</strong>
              </li>

              <li style={{ margin: 0 }}>
                In case of payment failure, you can use the same challan to make the
                payment <strong>within 7 days</strong> from the date of challan creation.
              </li>
            </ul>
          </div>

          <p style={{ marginTop: "16px", fontWeight: 600, color: "#555" }}>
            Please carefully confirm your payment mode before proceeding to the SBI Payment Gateway.
          </p>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
            I Agree
          </label>

          <div style={btnRow}>
            <button style={cancelBtnStyle} type="button" onClick={onCancel}>
              Cancel
            </button>
            <button
              style={proceedBtnStyle}
              type="button"
              disabled={!agreed}
              onClick={() => {
                if (agreed) onAgree();
              }}
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDeclarationModal;
